import React from 'react';

function FloorMap({ floor, currentPosition, targetPosition, pathNodes = [], floorLayouts = {} }) {
  // SVG dimensions
  const width = 600;
  const height = 400;

  // Filter path nodes relevant to this floor
  const floorNodes = pathNodes.filter(node => {
    if (node.type === 'stairs') {
      return node.floors && node.floors.includes(floor);
    }
    return node.floor === floor;
  });

  // Build path segments between consecutive nodes on this floor
  let pathD = '';
  if (floorNodes.length >= 2) {
    pathD = `M ${floorNodes[0].x} ${floorNodes[0].y}`;
    for (let i = 1; i < floorNodes.length; i++) {
      pathD += ` L ${floorNodes[i].x} ${floorNodes[i].y}`;
    }
  } else {
    // Determine whether current/target should be shown on this floor
    const renderCurrentOnFloor = !!(currentPosition && (currentPosition.floor === floor || (currentPosition.type === 'stairs' && currentPosition.floors?.includes?.(floor))));
    const renderTargetOnFloor = !!(targetPosition && (targetPosition.floor === floor || (targetPosition.type === 'stairs' && targetPosition.floors?.includes?.(floor))));

    // Fallback to simple line only when both endpoints are actually on this floor
    if (renderCurrentOnFloor && renderTargetOnFloor) {
      pathD = `M ${currentPosition.x} ${currentPosition.y} L ${targetPosition.x} ${targetPosition.y}`;
    }
  }

  return (
    <div className="border-2 border-primary/30 rounded-lg overflow-hidden bg-dark relative">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        style={{ minHeight: '300px' }}
      >
        {/* Background grid */}
        <defs>
          <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
            <path
              d="M 50 0 L 0 0 0 50"
              fill="none"
              stroke="#00ff41"
              strokeWidth="0.5"
              opacity="0.2"
            />
          </pattern>
        </defs>
        <rect width={width} height={height} fill="#0a0e14" />
        <rect width={width} height={height} fill="url(#grid)" />

        {/* Floor label */}
        <text
          x="20"
          y="30"
          fontSize="24"
          fontWeight="bold"
          fill="#00ff41"
          fontFamily="monospace"
        >
          LEVEL_{floor}
        </text>

        {/* Building rooms (decorative) - use per-floor layout when provided */}
        {(() => {
          const layout = floorLayouts[floor] || null;

          // helper to render generic svg element shapes from layout.elements
          const renderElement = (el, idx) => {
            const key = el.key || `el-${idx}`;
            const props = el.props || {};

            switch (el.type) {
              case 'rect':
                return <rect key={key} {...props} />;
              case 'circle':
                return <circle key={key} {...props} />;
              case 'path':
                // allow either d in props or el.d
                return <path key={key} d={el.d || props.d} {...props} />;
              case 'polygon':
                return <polygon key={key} points={el.points || props.points} {...props} />;
              case 'line':
                return <line key={key} {...props} />;
              case 'text':
                return (
                  <text key={key} {...props}>
                    {el.text}
                  </text>
                );
              case 'g':
              case 'group':
                return (
                  <g key={key} {...props}>
                    {(el.children || []).map((child, i) => renderElement(child, `${idx}-${i}`))}
                  </g>
                );
              case 'raw':
                return <g key={key} dangerouslySetInnerHTML={{ __html: el.svg || '' }} />;
              default:
                return null;
            }
          };

          if (layout) {
            if (Array.isArray(layout.elements)) {
              return layout.elements.map((el, idx) => renderElement(el, idx));
            }

            // legacy rooms fallback for backwards compatibility
            if (Array.isArray(layout.rooms)) {
              return layout.rooms.map((r, idx) => (
                <rect
                  key={`room-${idx}`}
                  x={r.x}
                  y={r.y}
                  width={r.width}
                  height={r.height}
                  fill={r.fill || '#151922'}
                  stroke={r.stroke || '#00ff41'}
                  strokeWidth={r.strokeWidth != null ? r.strokeWidth : '1.5'}
                  rx={r.rx != null ? r.rx : 3}
                />
              ));
            }
          }

          // default hard-coded rooms/corridor (keeps previous visuals)
          return (
            <>
              <rect x="50" y="80" width="150" height="100" fill="#151922" stroke="#00ff41" strokeWidth="1.5" rx="3" />
              <rect x="250" y="80" width="150" height="100" fill="#151922" stroke="#00ff41" strokeWidth="1.5" rx="3" />
              <rect x="450" y="80" width="100" height="100" fill="#151922" stroke="#00ff41" strokeWidth="1.5" rx="3" />
              
              <rect x="50" y="230" width="150" height="120" fill="#151922" stroke="#00ff41" strokeWidth="1.5" rx="3" />
              <rect x="250" y="230" width="150" height="120" fill="#151922" stroke="#00ff41" strokeWidth="1.5" rx="3" />
              <rect x="450" y="230" width="100" height="120" fill="#151922" stroke="#00ff41" strokeWidth="1.5" rx="3" />

              {/* Corridor */}
              <rect x="220" y="80" width="20" height="270" fill="#1f2937" stroke="#00ff41" strokeWidth="0.5" />
            </>
          );
        })()}

        {/* Path line */}
        {pathD && (
          <path
            d={pathD}
            stroke="#00d9ff"
            strokeWidth="4"
            fill="none"
            strokeDasharray="10,5"
            className="path-animate"
          />
        )}

        {/* Current position marker (only render if on this floor) */}
        {(() => {
          const renderCurrentOnFloor = !!(currentPosition && (currentPosition.floor === floor || (currentPosition.type === 'stairs' && currentPosition.floors?.includes?.(floor))));
          return renderCurrentOnFloor ? (
          <g>
            <circle
              cx={currentPosition.x}
              cy={currentPosition.y}
              r="15"
              fill="#00ff41"
              stroke="#00ff41"
              strokeWidth="3"
              opacity="0.8"
            />
            <circle
              cx={currentPosition.x}
              cy={currentPosition.y}
              r="10"
              fill="#0a0e14"
            />
            <text
              x={currentPosition.x}
              y={currentPosition.y + 35}
              fontSize="12"
              fontWeight="bold"
              textAnchor="middle"
              fill="#00ff41"
              fontFamily="monospace"
            >
              {currentPosition.type === 'stairs' ? '[STAIRS]' : '[START]'}
            </text>
          </g>
          ) : null;
        })()}

        {/* Target position marker (only render if on this floor) */}
        {(() => {
          const renderTargetOnFloor = !!(targetPosition && (targetPosition.floor === floor || (targetPosition.type === 'stairs' && targetPosition.floors?.includes?.(floor))));
          return renderTargetOnFloor ? (
            <g className="pulse">
              <circle
                cx={targetPosition.x}
                cy={targetPosition.y}
                r="15"
                fill="#ff0041"
                stroke="#ff0041"
                strokeWidth="3"
                opacity="0.8"
              />
              <circle
                cx={targetPosition.x}
                cy={targetPosition.y}
                r="10"
                fill="#0a0e14"
              />
              <text
                x={targetPosition.x}
                y={targetPosition.y + 35}
                fontSize="12"
                fontWeight="bold"
                textAnchor="middle"
                fill="#ff0041"
                fontFamily="monospace"
              >
                {targetPosition.type === 'stairs' ? '[STAIRS]' : (targetPosition.name || '').toUpperCase()}
              </text>
            </g>
          ) : null;
        })()}

        {/* Legend */}
        <g transform="translate(400, 15)">
          <rect width="180" height="55" fill="#151922" stroke="#00ff41" strokeWidth="1" rx="3" opacity="0.9" />
          <circle cx="20" cy="20" r="6" fill="#00ff41" />
          <text x="35" y="25" fontSize="11" fill="#00ff41" fontFamily="monospace">YOUR_POS</text>
          <circle cx="20" cy="40" r="6" fill="#ff0041" />
          <text x="35" y="45" fontSize="11" fill="#ff0041" fontFamily="monospace">TARGET</text>
        </g>
      </svg>

      {/* Character image overlays */}
      {currentPosition && currentPosition.type === 'character' && currentPosition.image && currentPosition.floor === floor && (
        <div 
          className="absolute"
          style={{
            left: `${(currentPosition.x / width) * 100}%`,
            top: `${(currentPosition.y / height) * 100}%`,
            transform: 'translate(-50%, -50%)'
          }}
        >
          <img 
            src={currentPosition.image}
            alt={currentPosition.name}
            className="w-12 h-12 object-cover rounded-full border-2 border-primary shadow-neon-green"
          />
        </div>
      )}
      
      {targetPosition && targetPosition.type === 'character' && targetPosition.image && targetPosition.floor === floor && (
        <div 
          className="absolute pulse"
          style={{
            left: `${(targetPosition.x / width) * 100}%`,
            top: `${(targetPosition.y / height) * 100}%`,
            transform: 'translate(-50%, -50%)'
          }}
        >
          <img 
            src={targetPosition.image}
            alt={targetPosition.name}
            className="w-12 h-12 object-cover rounded-full border-2 border-cyber-red shadow-neon-red"
          />
        </div>
      )}

      {/* Floor info */}
      <div className="bg-dark-lighter p-3 border-t border-primary/30">
        <div className="flex justify-between text-sm font-mono">
          <span className="text-matrix-green">
            CURRENT: <span className="font-bold text-primary">LEVEL_{currentPosition?.floor || '?'}</span>
          </span>
          <span className="text-matrix-green">
            TARGET: <span className="font-bold text-cyber-red">LEVEL_{targetPosition?.floor || '?'}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

export default FloorMap;
